'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { FeaturedReview } from '@nutralab/api';
import { Container, Heading, IconButton, useItemsPerView } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import { ReviewCard } from './_components/ReviewCard';
import styles from './ReviewBlock.module.scss';

import { getImageUrl } from '@/shared/utils';

const AUTOPLAY_INTERVAL_MS = 5000;

// Держим в синхроне с брейкпоинтом flex-basis у .track-item в ReviewBlock.module.scss

interface ReviewBlockProps extends React.HTMLAttributes<HTMLDivElement> {
    reviews: FeaturedReview[];
    noBackground?: boolean;
    headingLevel?: 1 | 2;
}

/** A carousel of featured customer reviews (home and reviews pages). */
export function ReviewBlock({
    reviews,
    className,
    noBackground,
    headingLevel = 2,
    ...props
}: ReviewBlockProps): React.ReactElement | null {
    const { t } = useTranslation();
    const itemsPerView = useItemsPerView(2);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [isPaused, setIsPaused] = React.useState(false);
    const [isJumping, setIsJumping] = React.useState(false);

    const count = reviews.length;
    const isLooped = count > itemsPerView;
    const slides = isLooped ? [...reviews, ...reviews.slice(0, itemsPerView)] : reviews;

    const jumpTo = React.useCallback((index: number, then?: () => void): void => {
        setIsJumping(true);
        setActiveIndex(index);
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                setIsJumping(false);
                then?.();
            }),
        );
    }, []);

    const next = React.useCallback((): void => {
        if (activeIndex >= count) {
            jumpTo(activeIndex - count, () => setActiveIndex((i) => i + 1));
            return;
        }
        setActiveIndex((i) => i + 1);
    }, [activeIndex, count, jumpTo]);

    const prev = (): void => {
        if (activeIndex === 0) {
            jumpTo(count, () => setActiveIndex(count - 1));
            return;
        }
        setActiveIndex((i) => i - 1);
    };

    const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>): void => {
        if (event.target === event.currentTarget && activeIndex >= count) {
            jumpTo(activeIndex - count);
        }
    };

    React.useEffect(() => {
        setActiveIndex(0);
    }, [itemsPerView]);

    React.useEffect(() => {
        if (isPaused || !isLooped) {
            return;
        }

        const id = setTimeout(next, AUTOPLAY_INTERVAL_MS);

        return () => clearTimeout(id);
    }, [activeIndex, isPaused, isLooped, next]);

    if (count === 0) {
        return null;
    }

    return (
        <div
            className={classNames(styles['review-block'], noBackground && styles['no-background'], className)}
            {...props}
        >
            <Container>
                <Heading level={headingLevel} className="mb-20" size="2xl" align="center" weight="semibold">
                    {t('landing.reviews.title', 'Últimas revisiones')}
                </Heading>

                <div
                    className={styles.wrapper}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div className={classNames(styles.viewport, 'mx-12')}>
                        <div
                            className={classNames(styles.track, isJumping && styles['no-transition'])}
                            style={{ transform: `translateX(-${activeIndex * (100 / itemsPerView)}%)` }}
                            onTransitionEnd={handleTransitionEnd}
                        >
                            {slides.map((review, index) => (
                                <div
                                    key={index < count ? `${review.offer_id}-${index}` : `clone-${index}`}
                                    className={styles['track-item']}
                                    aria-hidden={index >= count || undefined}
                                >
                                    <ReviewCard
                                        offerId={review.offer_id}
                                        imageSrc={getImageUrl(review.main_image)}
                                        title={review.title}
                                        authorName={review.public_name}
                                        description={review.text}
                                        rating={review.rating.average}
                                        learnMoreLabel={t('landing.review-card.learn-more', 'Aprende más')}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {isLooped && (
                        <>
                            <IconButton
                                aria-label={t('landing.reviews.previous')}
                                variant="outlined"
                                size="md"
                                className={classNames(styles.btn, styles['btn-prev'])}
                                onClick={prev}
                            >
                                <ChevronLeft />
                            </IconButton>

                            <IconButton
                                aria-label={t('landing.reviews.next')}
                                variant="outlined"
                                size="md"
                                className={classNames(styles.btn, styles['btn-next'])}
                                onClick={next}
                            >
                                <ChevronRight />
                            </IconButton>
                        </>
                    )}
                </div>
            </Container>
        </div>
    );
}
